import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Save,
  Building2,
  X,
  Check,
} from 'lucide-react';
import { quoteRequestsApi, suppliersApi, handleApiError } from '../lib/api';

interface Supplier {
  id: string;
  name: string;
  email: string;
  category?: string;
  isActive: boolean;
}

interface QuoteItem {
  id: string;
  description: string;
  specifications: string;
  quantity: number;
  unit: string;
  requiredBrand: string;
  notes: string;
}

export function QuotationNew() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectName, setProjectName] = useState('');
  const [requiredByDate, setRequiredByDate] = useState('');

  // Items
  const [items, setItems] = useState<QuoteItem[]>([
    {
      id: crypto.randomUUID(),
      description: '',
      specifications: '',
      quantity: 1,
      unit: 'und',
      requiredBrand: '',
      notes: '',
    },
  ]);

  // Suppliers
  const [availableSuppliers, setAvailableSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<Set<string>>(new Set());
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersApi.list({ isActive: 'true' });
      setAvailableSuppliers(response.data.data?.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        id: crypto.randomUUID(),
        description: '',
        specifications: '',
        quantity: 1,
        unit: 'und',
        requiredBrand: '',
        notes: '',
      },
    ]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof QuoteItem, value: any) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const toggleSupplier = (supplierId: string) => {
    const newSelected = new Set(selectedSupplierIds);
    if (newSelected.has(supplierId)) {
      newSelected.delete(supplierId);
    } else {
      newSelected.add(supplierId);
    }
    setSelectedSupplierIds(newSelected);
  };

  const filteredSuppliers = availableSuppliers.filter(
    (s) =>
      s.name.toLowerCase().includes(supplierSearch.toLowerCase()) ||
      s.email.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('El titulo es requerido');
      return;
    }

    const validItems = items.filter((item) => item.description.trim());
    if (validItems.length === 0) {
      setError('Debes agregar al menos un item');
      return;
    }

    setIsLoading(true);

    try {
      const response = await quoteRequestsApi.create({
        title: title.trim(),
        description: description.trim() || undefined,
        projectName: projectName.trim() || undefined,
        requiredByDate: requiredByDate || undefined,
        items: validItems.map((item) => ({
          description: item.description,
          specifications: item.specifications || undefined,
          quantity: item.quantity,
          unit: item.unit,
          requiredBrand: item.requiredBrand || undefined,
          notes: item.notes || undefined,
        })),
        supplierIds: Array.from(selectedSupplierIds),
      });

      const createdId = response.data.data?.quoteRequest?.id;
      if (createdId) {
        navigate(`/quotations/${createdId}`);
      } else {
        navigate('/quotations');
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const selectedSuppliers = availableSuppliers.filter((s) =>
    selectedSupplierIds.has(s.id)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/quotations')}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Nueva Solicitud de Cotizacion</h1>
          <p className="text-slate-400 mt-1">Completa los datos para crear la solicitud</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-400">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Informacion General</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Titulo *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Cotizacion materiales electricos proyecto X"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Proyecto
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Nombre del proyecto"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Fecha limite de respuesta
              </label>
              <input
                type="date"
                value={requiredByDate}
                onChange={(e) => setRequiredByDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Descripcion
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Detalles adicionales de la solicitud..."
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Items a Cotizar</h2>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Agregar Item
            </button>
          </div>

          <div className="space-y-4">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="bg-slate-800/50 rounded-lg p-4 border border-slate-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-emerald-400">
                    Item #{index + 1}
                  </span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="p-1 text-slate-400 hover:text-rose-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-6">
                    <label className="block text-xs text-slate-400 mb-1">
                      Descripcion *
                    </label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                      placeholder="Descripcion del item"
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-slate-400 mb-1">Cantidad</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)
                      }
                      min="0"
                      step="any"
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-slate-400 mb-1">Unidad</label>
                    <select
                      value={item.unit}
                      onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="und">Und</option>
                      <option value="m">Metro</option>
                      <option value="m2">m2</option>
                      <option value="m3">m3</option>
                      <option value="kg">Kg</option>
                      <option value="gl">Global</option>
                      <option value="rollo">Rollo</option>
                      <option value="caja">Caja</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-slate-400 mb-1">Marca</label>
                    <input
                      type="text"
                      value={item.requiredBrand}
                      onChange={(e) => updateItem(item.id, 'requiredBrand', e.target.value)}
                      placeholder="Opcional"
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="md:col-span-12">
                    <label className="block text-xs text-slate-400 mb-1">
                      Especificaciones tecnicas
                    </label>
                    <textarea
                      value={item.specifications}
                      onChange={(e) => updateItem(item.id, 'specifications', e.target.value)}
                      rows={2}
                      placeholder="Caracteristicas, dimensiones, referencias..."
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Suppliers */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              Proveedores ({selectedSupplierIds.size} seleccionados)
            </h2>
            <button
              type="button"
              onClick={() => setShowSupplierModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Seleccionar Proveedores
            </button>
          </div>

          {selectedSuppliers.length === 0 ? (
            <p className="text-slate-500 text-sm">
              No has seleccionado proveedores. Puedes agregarlos ahora o despues.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedSuppliers.map((supplier) => (
                <div
                  key={supplier.id}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg"
                >
                  <Building2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-white">{supplier.name}</span>
                  <button
                    type="button"
                    onClick={() => toggleSupplier(supplier.id)}
                    className="p-0.5 text-slate-400 hover:text-rose-400 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/quotations')}
            className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Crear Cotizacion
          </button>
        </div>
      </form>

      {/* Supplier Selection Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-slate-900 rounded-xl border border-slate-800 w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h2 className="text-lg font-semibold text-white">Seleccionar Proveedores</h2>
              <button
                onClick={() => setShowSupplierModal(false)}
                className="p-1 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-slate-800">
              <input
                type="text"
                placeholder="Buscar proveedor..."
                value={supplierSearch}
                onChange={(e) => setSupplierSearch(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {filteredSuppliers.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No se encontraron proveedores</p>
              ) : (
                <div className="space-y-1">
                  {filteredSuppliers.map((supplier) => {
                    const isSelected = selectedSupplierIds.has(supplier.id);
                    return (
                      <button
                        key={supplier.id}
                        type="button"
                        onClick={() => toggleSupplier(supplier.id)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                          isSelected
                            ? 'bg-emerald-500/10 border border-emerald-500/30'
                            : 'bg-slate-800/50 hover:bg-slate-800 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isSelected ? 'bg-emerald-500/20' : 'bg-slate-700'
                            }`}
                          >
                            <Building2
                              className={`w-4 h-4 ${
                                isSelected ? 'text-emerald-400' : 'text-slate-400'
                              }`}
                            />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-white">{supplier.name}</p>
                            <p className="text-xs text-slate-400">{supplier.email}</p>
                          </div>
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-emerald-400" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setShowSupplierModal(false)}
                className="w-full px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
              >
                Listo ({selectedSupplierIds.size} seleccionados)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
